// packages import
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField
} from '@mui/material'
import { FC, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { endOfDay } from 'date-fns'

// component import
import { CustomModal } from '../../../components/common/CustomModal'
import { ConfirmationModal } from '../../../components/common/ConfirmationModal'
import { Alert } from '../../../components/common/Alert'
import { EmptyData } from '../../../components/common/EmptyData'

// others
import { BillingCycle, User, useSearchUserLazyQuery, useGenerateBillsForAttorneysMutation } from '../../../../generated'
import { Billing_Cycle_Options, SEARCHABLE_USER_ROLES } from '../../../constants'
import { GRAPHQL_QUERY_POLICY } from '../../../constants'
import { flexCenter } from '../../../theme/styleConstants'

interface GenerateCollectionBillsModalProps {
  isOpen: boolean
  onClose: () => void
  refetch?: () => void
}

interface UserBillingCycle {
  userId: string
  billingCycle: BillingCycle
  customDate?: string
}

export const GenerateCollectionBillsModal: FC<GenerateCollectionBillsModalProps> = ({ isOpen, onClose, refetch }) => {
  const navigate = useNavigate()
  const [collectionUsers, setCollectionUsers] = useState<User[]>([])
  const [userBillingCycles, setUserBillingCycles] = useState<Map<string, UserBillingCycle>>(new Map())
  const [confirmingUserId, setConfirmingUserId] = useState<string | null>(null)
  const [generatingUserId, setGeneratingUserId] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successAttorneyId, setSuccessAttorneyId] = useState<string | null>(null)
  const [showNoBillsModal, setShowNoBillsModal] = useState(false)
  const [noBillsAttorneyId, setNoBillsAttorneyId] = useState<string | null>(null)

  const [searchUsers, { loading: searchingUsers }] = useSearchUserLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onCompleted: data => {
      if (data?.searchUser?.users) {
        // Filter out null values and ensure type safety
        const validUsers = (data.searchUser.users || []).filter(
          (user): user is User => user !== null && user !== undefined && !!user.id
        )
        setCollectionUsers(validUsers)
        // Initialize billing cycles for each user
        const cyclesMap = new Map<string, UserBillingCycle>()
        validUsers.forEach(user => {
          if (user?.id) {
            cyclesMap.set(user.id, {
              userId: user.id,
              billingCycle: BillingCycle.EndOfMonth
            })
          }
        })
        setUserBillingCycles(cyclesMap)
      }
    },
    onError: error => {
      Alert.error(error.message || 'Failed to fetch collection users')
      setCollectionUsers([])
    }
  })

  const [generateBillsForAttorneys, { loading: generatingBills }] = useGenerateBillsForAttorneysMutation({
    onError: error => {
      const errorMessage = error.message || 'Failed to generate bills'
      // Check if error is about no bills to generate
      if (
        errorMessage.toLowerCase().includes('no bills') ||
        errorMessage.toLowerCase().includes('no matters') ||
        errorMessage.toLowerCase().includes('no cases') ||
        errorMessage.toLowerCase().includes('no active cases')
      ) {
        setGeneratingUserId(null)
        setNoBillsAttorneyId(confirmingUserId)
        setShowNoBillsModal(true)
        setConfirmingUserId(null)
      } else {
        Alert.error(errorMessage)
        setGeneratingUserId(null)
        setConfirmingUserId(null)
      }
    },
    onCompleted: data => {
      const { generateBillsForAttorneys } = data || {}
      const { response } = generateBillsForAttorneys || {}

      if (response?.status === 200 && confirmingUserId) {
        setGeneratingUserId(null)
        setConfirmingUserId(null)
        // Show success modal with acknowledgment message
        setSuccessAttorneyId(confirmingUserId)
        setShowSuccessModal(true)
        if (refetch) {
          refetch()
        }
      }
    }
  })

  useEffect(() => {
    if (isOpen) {
      // Fetch collection users when modal opens
      searchUsers({
        variables: {
          searchUserInput: {
            searchTerm: 'Collections -',
            roles: SEARCHABLE_USER_ROLES // Pass all searchable roles to effectively search all users
          }
        }
      })
    } else {
      // Reset state when modal closes
      setCollectionUsers([])
      setUserBillingCycles(new Map())
      setConfirmingUserId(null)
      setGeneratingUserId(null)
      setShowSuccessModal(false)
      setSuccessAttorneyId(null)
      setShowNoBillsModal(false)
      setNoBillsAttorneyId(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const handleBillingCycleChange = (userId: string, billingCycle: BillingCycle) => {
    setUserBillingCycles(prev => {
      const newMap = new Map(prev)
      const current = newMap.get(userId) || { userId, billingCycle: BillingCycle.EndOfMonth }
      newMap.set(userId, {
        ...current,
        billingCycle,
        customDate: billingCycle === BillingCycle.CustomDate ? current.customDate : undefined
      })
      return newMap
    })
  }

  const handleCustomDateChange = (userId: string, date: Date | null) => {
    setUserBillingCycles(prev => {
      const newMap = new Map(prev)
      const current = newMap.get(userId) || { userId, billingCycle: BillingCycle.EndOfMonth }
      newMap.set(userId, {
        ...current,
        customDate: date ? endOfDay(new Date(date)).toISOString() : undefined
      })
      return newMap
    })
  }

  const handleGenerateBillsClick = (userId: string) => {
    setConfirmingUserId(userId)
  }

  const handleConfirmGenerate = async () => {
    if (!confirmingUserId) return

    const userCycle = userBillingCycles.get(confirmingUserId)
    if (!userCycle) {
      Alert.error('Billing cycle not set for this user')
      return
    }

    if (userCycle.billingCycle === BillingCycle.CustomDate && !userCycle.customDate) {
      Alert.error('Please select a custom date')
      return
    }

    setGeneratingUserId(confirmingUserId)

    await generateBillsForAttorneys({
      variables: {
        generateBillsForAttorneysInput: {
          attorneyIds: [confirmingUserId],
          billingCycle: userCycle.billingCycle,
          customDate: userCycle.customDate
        }
      }
    })
  }

  const handleCancelConfirm = () => {
    setConfirmingUserId(null)
  }

  const handleSuccessModalConfirm = () => {
    setShowSuccessModal(false)
    onClose()
    if (successAttorneyId) {
      navigate(`/billing/bills?attorneyId=${successAttorneyId}&currentPage=1`)
    }
  }

  const handleSuccessModalCancel = () => {
    setShowSuccessModal(false)
    setSuccessAttorneyId(null)
  }

  const handleNoBillsModalClose = () => {
    setShowNoBillsModal(false)
    setNoBillsAttorneyId(null)
  }

  const handleNoBillsViewBills = () => {
    setShowNoBillsModal(false)
    if (noBillsAttorneyId) {
      window.open(`/billing/bills?attorneyId=${noBillsAttorneyId}&currentPage=1`, '_blank')
    }
    setNoBillsAttorneyId(null)
  }

  // const currentUserCycle = confirmingUserId ? userBillingCycles.get(confirmingUserId) : null
  const currentUser = confirmingUserId ? collectionUsers.find(u => u.id === confirmingUserId) : null
  const successUser = successAttorneyId ? collectionUsers.find(u => u.id === successAttorneyId) : null
  const noBillsUser = noBillsAttorneyId ? collectionUsers.find(u => u.id === noBillsAttorneyId) : null

  return (
    <>
      <CustomModal title="Generate Collection Bills" isOpen={isOpen} handleClose={onClose} maxWidth="lg">
        <Box sx={{ p: 2 }}>
          {searchingUsers ? (
            <Box sx={{ ...flexCenter, py: 4 }}>
              <CircularProgress />
            </Box>
          ) : collectionUsers.length === 0 ? (
            <Box sx={{ ...flexCenter, py: 4 }}>
              <EmptyData title="No collection users found" />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Billing Cycle</TableCell>
                    <TableCell>Custom Date</TableCell>
                    <TableCell align="center" sx={{ minWidth: 180 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {collectionUsers.map(user => {
                    if (!user?.id) return null
                    const userCycle = userBillingCycles.get(user.id) || {
                      userId: user.id,
                      billingCycle: BillingCycle.EndOfMonth
                    }
                    const isGenerating = generatingUserId === user.id

                    return (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {user.firstName} {user.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Billing Cycle</InputLabel>
                            <Select
                              value={userCycle.billingCycle}
                              label="Billing Cycle"
                              onChange={e => handleBillingCycleChange(user.id, e.target.value as BillingCycle)}
                              disabled={isGenerating}
                            >
                              {Billing_Cycle_Options.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                              <MenuItem value={BillingCycle.CustomDate}>Custom</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {userCycle.billingCycle === BillingCycle.CustomDate ? (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                label="Custom Date"
                                value={userCycle.customDate ? new Date(userCycle.customDate) : null}
                                onChange={date => handleCustomDateChange(user.id, date)}
                                disabled={isGenerating}
                                renderInput={params => <TextField {...params} size="small" sx={{ minWidth: 200 }} />}
                              />
                            </LocalizationProvider>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleGenerateBillsClick(user.id)}
                            disabled={isGenerating || generatingBills}
                            endIcon={isGenerating && <CircularProgress size={16} color="inherit" />}
                          >
                            Generate Bills
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </CustomModal>

      <ConfirmationModal
        isOpen={confirmingUserId !== null}
        title="Generate Collection Bills"
        message={`Are you sure you want to generate final bills (BILLED status) for ${
          currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'this attorney'
        }? This action cannot be undone.`}
        confirmText="Generate Bills"
        cancelText="Cancel"
        variant="danger"
        isLoading={generatingBills && generatingUserId === confirmingUserId}
        onConfirm={handleConfirmGenerate}
        onCancel={handleCancelConfirm}
      />

      <ConfirmationModal
        isOpen={showSuccessModal}
        title="Bills Generation Started"
        message={`Bills generation has started for ${
          successUser ? `${successUser.firstName} ${successUser.lastName}` : 'the attorney'
        }. This process will be completed in a few moments.\n\nYou can view the existing collection bills by clicking the button below.`}
        confirmText="View Bills"
        cancelText="Close"
        variant="danger"
        isLoading={false}
        onConfirm={handleSuccessModalConfirm}
        onCancel={handleSuccessModalCancel}
      />

      <ConfirmationModal
        isOpen={showNoBillsModal}
        title="No Bills to Generate"
        message={`There are No Matters to generate bills in the selected billing cycle for ${
          noBillsUser ? `${noBillsUser.firstName} ${noBillsUser.lastName}` : 'this attorney'
        }. Please select a different billing cycle or custom date.\n\nYou can view the existing collection bills by clicking the button below.`}
        confirmText="View Bills"
        cancelText="Close"
        variant="danger"
        isLoading={false}
        onConfirm={handleNoBillsViewBills}
        onCancel={handleNoBillsModalClose}
      />
    </>
  )
}

export default GenerateCollectionBillsModal
